import Vue from "vue";
import _ from "lodash";
import moment from "moment";
import {db, docFromSnapshot, docsFromSnapshot, storage} from "@/firebase";
import { compareEmployeeNames } from "@/helpers/bookingSearch";
import {
  isEmployeeRosterCleanupEvent,
  summarizeEmployeeBookingBlock,
  summarizeEmployeeRosterCleanup
} from "@/helpers/employeeRosterCleanup";
import { omitUndefinedDeep } from "@/helpers/helpers";
import { scopedResourceFilters } from "@/helpers/onboard";
import {
  buildMissingRosterEvents,
  rosterDefaultsUnchanged
} from "@/helpers/rosterDefaults";

const FIRESTORE_EMPLOYEES_COLLECTION = "resources";

const state = {
  employees: {}
};

const getters = {
  employees: state => state.employees,
  employeesList: state =>
    Object.values(state.employees).sort(compareEmployeeNames),
  employeeById: state => id => state.employees[id]
};

const SET_EMPLOYEES = "SET_EMPLOYEES";
const SET_EMPLOYEE = "SET_EMPLOYEE";
const REMOVE_EMPLOYEE = "REMOVE_EMPLOYEE";

function requiresUpload(image) {
  return image ? !image.startsWith("http") : false;
}

async function uploadImage(context, employee) {
  await storage
    .ref()
    .child(`resourceImage/${employee.storeId}/${employee._id}`)
    .putString(employee.image, "data_url");

  const logoDownloadUrl = await storage
    .ref()
    .child(`resourceImage/${employee.storeId}/${employee._id}`)
    .getDownloadURL();

  return {
    ...employee,
    image: logoDownloadUrl
  };
}

function storeIdFrom(context, employee) {
  return (
    (employee && employee.storeId) ||
    _.get(context, "rootGetters.userClaims.storeId") ||
    null
  );
}

async function loadScopedEmployeeDocs(collectionName, storeId, resourceId) {
  const filters = scopedResourceFilters(storeId, resourceId);
  if (!filters) return [];
  let query = db.collection(collectionName);
  filters.forEach(pair => {
    query = query.where(pair[0], pair[1], pair[2]);
  });
  const snap = await query.get();
  return snap.docs.map(doc => ({
    _id: doc.id,
    ...doc.data(),
    ref: doc.ref
  }));
}

async function syncEmployeeRosterEvents(context, employee, opts) {
  if (!employee || !employee._id) return;
  const shop = context.rootGetters["shops/shop"] || {};
  const storeId = storeIdFrom(context, employee);
  let existingEvents = [];
  if (!(opts && opts.assumeEmpty)) {
    existingEvents = await loadScopedEmployeeDocs(
      "events",
      storeId,
      employee._id
    );
  }
  const drafts = buildMissingRosterEvents({
    employee,
    shop,
    today: moment.utc().format("YYYY-MM-DD"),
    existingEvents
  });
  if (!drafts.length) return;

  const now = +new Date();
  const chunkSize = 400;
  for (let i = 0; i < drafts.length; i += chunkSize) {
    const batch = db.batch();
    drafts.slice(i, i + chunkSize).forEach(event => {
      const ref = db.collection("events").doc();
      batch.set(ref, {
        ...event,
        _id: ref.id,
        tenantId: employee.tenantId,
        storeId: employee.storeId,
        created: now,
        lastModified: now
      });
    });
    await batch.commit();
  }
}

async function loadEmployeeEvents(context, employee) {
  return loadScopedEmployeeDocs(
    "events",
    storeIdFrom(context, employee),
    employee && employee._id
  );
}

/** Firestore list rules require storeId. A resourceId-only query is denied. */
async function loadEmployeeBookings(context, employee) {
  const storeId = storeIdFrom(context, employee);
  const employeeId = employee && employee._id;
  if (!storeId || !employeeId) return [];
  try {
    return await loadScopedEmployeeDocs("bookings", storeId, employeeId);
  } catch (_error) {
    const since = moment
      .utc()
      .subtract(1, "day")
      .startOf("day")
      .toISOString();
    const snap = await db
      .collection("bookings")
      .where("storeId", "==", storeId)
      .where("start", ">=", since)
      .get();
    return snap.docs
      .map(doc => ({
        _id: doc.id,
        ...doc.data(),
        ref: doc.ref
      }))
      .filter(doc => doc.resourceId === employeeId);
  }
}

const actions = {
  async createEmployee(context, employee) {
    const ref = db.collection(FIRESTORE_EMPLOYEES_COLLECTION).doc();
    employee = {
      ...employee,
      _id: ref.id,
      tenantId: context.rootGetters.tenantId,
      storeId: _.get(context, "rootGetters.userClaims.storeId"),
      lastModified: +new Date(),
      created: +new Date()
    };
    employee = employee.image ? await uploadImage(context, employee) : employee;
    employee = omitUndefinedDeep(employee);
    await ref.set(employee);
    await syncEmployeeRosterEvents(context, employee, { assumeEmpty: true });
    context.commit(SET_EMPLOYEE, employee);
  },
  async fetchEmployees(context) {
    const snap = await db
      .collection(FIRESTORE_EMPLOYEES_COLLECTION)
      .where("storeId", "==", context.rootGetters.userClaims.storeId)
      .get();
    const docs = docsFromSnapshot(snap);
    context.commit(SET_EMPLOYEES, docs);
  },
  async fetchAnyEmployee(context) {
    const anyResource = {
      "Any": {
        description: "",
        image: undefined,
        name: "Any", availableForOnlineBookings: true, _id: "Any"
      }
    }
    context.commit(SET_EMPLOYEES, anyResource);
  },
  async fetchAllWithAnyEmployee(context) {
    const anyResource = {
      "Any": {
        description: "",
        image: undefined,
        name: "Any", availableForOnlineBookings: true, _id: "Any"
      }
    }
    const snap = await db
      .collection(FIRESTORE_EMPLOYEES_COLLECTION)
      .where("storeId", "==", context.rootGetters.userClaims.storeId)
      .get();
    const docs = docsFromSnapshot(snap);
    context.commit(SET_EMPLOYEES, {...docs, ...anyResource});
  },
  async fetchEmployee(context, id) {
    const snap = await db
      .collection(FIRESTORE_EMPLOYEES_COLLECTION)
      .doc(id)
      .get();
    const doc = docFromSnapshot(snap);
    context.commit(SET_EMPLOYEE, doc);
  },
  async updateEmployee(context, employee) {
    const previous = context.getters.employeeById(employee && employee._id);
    const updatedEmployee = omitUndefinedDeep(
      requiresUpload(employee.image)
        ? await uploadImage(context, employee)
        : employee
    );
    updatedEmployee.lastModified = +new Date();

    await db
      .collection(FIRESTORE_EMPLOYEES_COLLECTION)
      .doc(updatedEmployee._id)
      .set(updatedEmployee);
    if (!rosterDefaultsUnchanged(previous, updatedEmployee)) {
      await syncEmployeeRosterEvents(context, updatedEmployee);
    }
    context.commit(SET_EMPLOYEE, updatedEmployee);
  },
  async previewEmployeeDelete(context, employee) {
    if (!employee || !employee._id) {
      return {
        roster: summarizeEmployeeRosterCleanup([]),
        bookings: summarizeEmployeeBookingBlock([])
      };
    }
    const events = await loadEmployeeEvents(context, employee);
    const bookings = await loadEmployeeBookings(context, employee);
    return {
      roster: summarizeEmployeeRosterCleanup(events),
      bookings: summarizeEmployeeBookingBlock(bookings)
    };
  },
  async deleteEmployee(context, employee) {
    if (!employee || !employee._id) {
      throw new Error("Employee id is missing.");
    }

    const bookings = await loadEmployeeBookings(context, employee);
    const bookingBlock = summarizeEmployeeBookingBlock(bookings);
    if (bookingBlock.blocked) {
      throw new Error("They still have confirmed bookings.");
    }

    const events = await loadEmployeeEvents(context, employee);
    const rosterEvents = events.filter(isEmployeeRosterCleanupEvent);
    const chunkSize = 400;

    for (let i = 0; i < rosterEvents.length; i += chunkSize) {
      const slice = rosterEvents.slice(i, i + chunkSize);
      const batch = db.batch();
      slice.forEach(event => {
        batch.delete(event.ref || db.collection("events").doc(event._id));
      });
      const isLast = i + chunkSize >= rosterEvents.length;
      if (isLast) {
        batch.delete(
          db.collection(FIRESTORE_EMPLOYEES_COLLECTION).doc(employee._id)
        );
      }
      await batch.commit();
      slice.forEach(event => {
        context.commit(
          "roster/REMOVE_ROSTER_EVENT",
          { _id: event._id },
          { root: true }
        );
      });
    }

    if (!rosterEvents.length) {
      await db
        .collection(FIRESTORE_EMPLOYEES_COLLECTION)
        .doc(employee._id)
        .delete();
    }

    context.commit(REMOVE_EMPLOYEE, employee._id);
  },


};

const mutations = {
  [SET_EMPLOYEES](state, employees) {
    state.employees = employees;
  },
  [SET_EMPLOYEE](state, employee) {
    Vue.set(state.employees, employee._id, employee);
  },
  [REMOVE_EMPLOYEE](state, id) {
    state.employees = _.omit(state.employees, id);
  }
};

export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
};
